import type {
  TaskDirection,
  TaskStatus,
  TrackedTask,
} from '@/lib/api/tasks.js';

import { doneThisWeek, isOpen } from './task-dates.js';
import { STATUS_OPTIONS } from './task-format.js';

/**
 * The address of the Tasks page: the Board view (the list is the default and is left out), the task
 * open in the panel, and the filters. `author` is the Person filter: the author of a task for the
 * user, or the assignee of a task the user gave. `completed: 'all'` shows every completed task.
 */
export interface TasksSearch {
  view?: 'board';
  task?: string;
  source?: string;
  priority?: string;
  author?: string;
  q?: string;
  direction?: TaskDirection;
  status?: TaskStatus;
  completed?: 'all';
}

const TEXT_KEYS = ['task', 'source', 'priority', 'author', 'q'] as const;

const DIRECTIONS: readonly string[] = ['to_me', 'from_me'];

/**
 * Keeps the Board view, every non-empty text value the page knows, a known direction and status,
 * and Show all completed; drops the rest.
 */
export function parseTasksSearch(raw: Record<string, unknown>): TasksSearch {
  const search: TasksSearch = raw.view === 'board' ? { view: 'board' } : {};
  for (const key of TEXT_KEYS) {
    const value = raw[key];
    if (typeof value === 'string' && value.length > 0) search[key] = value;
  }
  if (DIRECTIONS.includes(raw.direction as string))
    search.direction = raw.direction as TaskDirection;
  if (STATUS_OPTIONS.some((option) => option.value === raw.status))
    search.status = raw.status as TaskStatus;
  if (raw.completed === 'all') search.completed = 'all';
  return search;
}

/** Whether any filter is set; the view and the open task are not filters. */
export function hasFilters(search: TasksSearch): boolean {
  return [
    search.source,
    search.priority,
    search.author,
    search.q,
    search.direction,
    search.status,
    search.completed,
  ].some((value) => value !== undefined);
}

/** The address without the open task: it changes with every filter and with the view. */
export function addressOf(search: TasksSearch): string {
  return JSON.stringify([
    search.view,
    search.source,
    search.priority,
    search.author,
    search.q,
    search.direction,
    search.status,
    search.completed,
  ]);
}

/** The other person of a task: who gave a task for the user, or who must do a task the user gave. */
export function counterpartOf(task: TrackedTask): string | null {
  return task.direction === 'from_me' ? task.assignee : task.author;
}

/** Whether a task's words or the places of its other sources hold the search text, ignoring case. */
function matchesText(task: TrackedTask, text: string): boolean {
  const needle = text.trim().toLocaleLowerCase();
  return [
    task.summary,
    task.placeLabel,
    task.author,
    task.assignee,
    task.excerpt,
    ...task.links.map((link) => link.placeLabel),
  ].some((value) => value?.toLocaleLowerCase().includes(needle) ?? false);
}

/** Whether a task came from a source, as its own or as one of its other sources. */
function hasSource(task: TrackedTask, source: string): boolean {
  return (
    task.source === source || task.links.some((link) => link.source === source)
  );
}

/** What the completed-task rule reads besides the address. */
export interface FilterContext {
  nowMs: number;
  /** Completed tasks that closed while the list was shown; they stay until the address changes. */
  kept: ReadonlySet<string>;
}

/**
 * Whether a completed task (Done or Won't do) shows. Show all completed, a status filter (every task
 * of that status, at any age) and a task kept in place show it; otherwise the list hides it, and the
 * board shows Won't do (folded away) and Done for 7 days after it was done.
 */
function completedShows(
  task: TrackedTask,
  search: TasksSearch,
  context: FilterContext,
): boolean {
  if (search.completed === 'all' || search.status !== undefined) return true;
  if (context.kept.has(task.id)) return true;
  if (search.view !== 'board') return false;
  return task.status === 'wont_do' || doneThisWeek(task, context.nowMs);
}

/**
 * The tasks that pass every filter set, in their order. A task kept in place passes the status
 * filter too: its status is what changed.
 */
export function filterTasks(
  tasks: readonly TrackedTask[],
  search: TasksSearch,
  context: FilterContext,
): TrackedTask[] {
  return tasks.filter(
    (task) =>
      (isOpen(task) || completedShows(task, search, context)) &&
      (search.source === undefined || hasSource(task, search.source)) &&
      (search.priority === undefined || task.priority === search.priority) &&
      (search.direction === undefined || task.direction === search.direction) &&
      (search.status === undefined ||
        task.status === search.status ||
        context.kept.has(task.id)) &&
      (search.author === undefined || counterpartOf(task) === search.author) &&
      (search.q === undefined || matchesText(task, search.q)),
  );
}
